import { z } from 'zod';
import { createShipmentDraftInputSchema } from './shipment-input.schema.js';
import { shipmentEnvironmentSchema, shipmentCategoryForWeight, } from '../shared/shipment-category.js';
import { unifiedShipmentDraftSchema, } from './unified-shipment-draft.schema.js';
import { createInitialTimeline } from '../shared/shipment-status.js';
export const buildShipmentDraftParamsSchema = z
    .object({
    input: createShipmentDraftInputSchema, shipmentId: z.string().min(1), trackingNumber: z.string().min(1),
    userId: z.string().min(1), nowIso: z.string().datetime(), authoritativePrice: z.number().finite().positive().optional(),
    environment: shipmentEnvironmentSchema.optional(),
    /** #2191 — lien VÉRIFIÉ par l'appelant, jamais `input.replacesShipmentId` tel quel. */
    replacesShipmentId: z.string().min(1).optional(),
})
    .strict();
/**
 * Produit les données minimales nécessaires au recalcul du prix côté serveur.
 * Les prix fournis par le client sont volontairement exclus de cette projection.
 */
export function projectShipmentDraftForPricing(input) {
    return {
        customerInfo: {
            sender: { address: input.sender.address },
            receiver: { address: input.receiver.address },
        },
        parcelInfo: {
            weight: input.parcel.weightKg,
            options: {
                fragileHandling: input.parcel.fragile === true,
                reinforcedPackaging: input.parcel.reinforcedPackaging === true,
            },
        },
        standardData: {
            serviceType: input.service.dpdServiceType,
            befretDeliveryMethod: input.service.befretDeliveryMethod,
        },
    };
}
function promotionData(input, gross, nowIso) {
    // Les aperçus de promotion conservent l’intention du client pour une vérification
    // ultérieure par le serveur ; la remise obtenue ne fait jamais autorité.
    const preview = input.promotionPreview ?? [];
    const codes = input.promotionCodes;
    if (!preview.length && !codes.length)
        return undefined;
    const totalDiscount = preview.reduce((sum, promotion) => sum + Math.max(0, promotion.discountAmount), 0);
    return {
        applied: true,
        codes,
        automaticPromotions: preview
            .filter((item) => item.type === 'automatic' && item.promotionId)
            .map((item) => item.promotionId),
        totalDiscount,
        originalPrice: gross,
        finalPrice: Math.max(0, gross - totalDiscount),
        appliedAt: nowIso,
        details: preview,
        authoritative: false,
    };
}
/**
 * Construit sans effet de bord le document initial prêt à persister. L’appelant injecte
 * l’identité, le tracking, l’heure et le prix autoritaires ; le builder ne contacte aucun service.
 * Le résultat est reparsé par le schéma strict avant d’être retourné.
 */
export function buildShipmentDraft(params) {
    const { input, shipmentId, trackingNumber, userId, nowIso } = params;
    const category = shipmentCategoryForWeight(input.parcel.weightKg);
    const standard = category === 'standard';
    if (standard &&
        (!Number.isFinite(params.authoritativePrice) || Number(params.authoritativePrice) <= 0)) {
        throw new Error('Prix serveur requis pour un shipment standard');
    }
    const type = standard
        ? input.service.dpdServiceType === 'befret_home'
            ? 'befret_home'
            : input.service.dpdServiceType === 'home_pickup'
                ? 'standard_home'
                : 'standard_shop'
        : 'heavy_specialist';
    // Le statut du brouillon décrit le premier état exploitable de chaque parcours métier.
    const status = standard
        ? {
            current: 'ready_for_deposit',
            phase: 'dpd_collection',
            label: 'Prêt pour dépôt',
            description: 'Commande finalisée, étiquette générée',
            isTerminal: false,
        }
        : {
            current: 'heavy_request_received',
            phase: 'heavy_processing',
            label: 'Demande reçue',
            description: "Demande colis lourd en cours d'analyse",
            isTerminal: false,
        };
    const timeline = createInitialTimeline(category);
    const gross = Number(params.authoritativePrice) || 0;
    const promotions = promotionData(input, gross, nowIso);
    const collectionScheduled = input.service.dpdServiceType === 'home_pickup' && input.service.collectionDate
        ? {
            scheduledDate: input.service.collectionDate,
            // 🔴 #1728 F — PLUS DE CRÉNEAU INVENTÉ, jumeau de #1730 ⑥⑦.
            //
            // Cette ligne écrivait `'08:00-18:00'` sur 100 % des collectes à
            // domicile du tunnel STANDARD : aucun producteur ne renseigne
            // `collectionTimeWindow` (ni `formControlName`, ni `fb.group`, ni
            // gabarit — seul le flux ASSISTÉ le porte, et par une autre
            // callable). Le client lisait donc, sur l'écran de paiement
            // (`payment.component.html`), un créneau que personne n'avait choisi
            // et que DPD n'avait pas proposé — une promesse par colis fabriquée
            // de toutes pièces.
            //
            // #1730 ⑥⑦ a supprimé exactement ce littéral de la branche assistée
            // (`functions/src/functions/assisted/index.ts`) ; il est resté ici.
            // Règle du dépôt : sans donnée, on n'invente pas. L'affichage retombe
            // déjà sur un libellé générique (« entre 9h et 18h ») — une
            // information, pas un engagement.
            ...(input.service.collectionTimeWindow
                ? { timeWindow: input.service.collectionTimeWindow }
                : {}),
            address: input.sender.address,
            status: 'scheduled',
            instructions: input.service.collectionInstructions || '',
        }
        : undefined;
    const draft = {
        id: shipmentId,
        trackingNumber,
        userId,
        category,
        type,
        businessModel: standard ? 'immediate_payment' : 'quote_based',
        transportProvider: standard ? 'dpd' : 'specialist_partner',
        timestamps: { createdAt: nowIso, updatedAt: nowIso },
        status,
        /**
         * #1401 — la phase RACINE, posée DÈS LA CRÉATION.
         *
         * Sans elle, le document est invisible de toutes les listes staff jusqu'à
         * sa première transition : `getAllShipmentsForList` filtre
         * `where('currentPhase','in',…)` côté serveur, et Firestore ne sait pas
         * interroger une chaîne de replis. Toute la fenêtre « créé, payé, pas
         * encore collecté » disparaissait — c'est le stock de #1381, et il se
         * reconstituait encore le 2026-08-24 (3 colis de production créés ce
         * jour-là, sans `currentPhase`).
         *
         * Elle se dérive du statut, comme partout ailleurs : ici `status.phase`
         * EST cette dérivation (`ready_for_deposit` → `dpd_collection`,
         * `heavy_request_received` → `heavy_processing`). Une seule décision,
         * écrite aussi là où les listes savent la lire — pas un second écrivain.
         * Le miroir serveur `STATUS_TO_SHIPMENT_PHASE`
         * (`functions/src/domains/shipment/shipment-phase.ts`) verrouille l'accord.
         */
        currentPhase: status.phase,
        phase: { current: status.phase, progress: 0, timeline },
        statusHistory: [],
        customerInfo: {
            sender: input.sender,
            receiver: input.receiver,
            preferences: {
                language: input.preferences?.language || 'fr',
                timezone: input.preferences?.timezone || 'Europe/Brussels',
            },
        },
        parcelInfo: {
            weight: input.parcel.weightKg,
            ...(input.parcel.dimensions && { dimensions: input.parcel.dimensions }),
            description: input.parcel.description,
            ...(input.parcel.contentType && { contentType: input.parcel.contentType }),
            deliveryMethod: input.service.befretDeliveryMethod,
            ...(input.parcel.items?.length && { items: input.parcel.items }),
            ...(input.parcel.declaredValue && {
                value: {
                    amount: input.parcel.declaredValue,
                    currency: 'EUR',
                    declared: true,
                    insured: false,
                },
            }),
            options: {
                reinforcedPackaging: input.parcel.reinforcedPackaging,
                fragileHandling: input.parcel.fragile,
                ...(input.parcel.specialInstructions && {
                    specialInstructions: input.parcel.specialInstructions,
                }),
                requiresSignature: true,
                saturdayDelivery: false,
            },
        },
        ...(standard
            ? {
                standardData: {
                    serviceType: input.service.dpdServiceType,
                    befretDeliveryMethod: input.service.befretDeliveryMethod,
                    ...(input.service.pickupPoint && { pickupPoint: input.service.pickupPoint }),
                    ...(collectionScheduled && { collectionScheduled }),
                    pricing: {
                        basePrice: gross,
                        weightSurcharge: 0,
                        serviceFee: 0,
                        taxes: 0,
                        total: gross,
                        currency: 'EUR',
                        paymentMethod: 'card',
                    },
                },
            }
            : {
                heavyData: {
                    processingInfo: { processingType: 'custom_shipping', processingStarted: nowIso },
                    customRequirements: [],
                    documents: { assessmentPhotos: [], packagingPhotos: [], deliveryPhotos: [] },
                    communications: [],
                    backofficeInfo: {
                        internalNotes: [],
                        specialRequirements: [],
                        flags: {
                            needsSpecialHandling: true,
                            requiresInstallation: false,
                            requiresDisassembly: false,
                            hasHazardousMaterials: false,
                            requiresPermits: false,
                        },
                    },
                },
            }),
        ...(promotions && { promotions }),
        metadata: {
            version: '1.0',
            source: 'web_form',
            environment: params.environment || 'development',
            flags: {
                testShipment: false,
                mockData: false,
                archived: false,
                migrated: false,
                validated: true,
            },
            tags: [
                standard ? 'standard_parcel' : 'heavy_parcel',
                `dpd_service:${input.service.dpdServiceType}`,
                `befret_delivery:${input.service.befretDeliveryMethod}`,
            ],
        },
        ...(input.userLocation !== undefined && { userLocation: input.userLocation }),
        ...(params.replacesShipmentId !== undefined && { replacesShipmentId: params.replacesShipmentId }),
    };
    return unifiedShipmentDraftSchema.parse(draft);
}
